import type { Bilingual, Locale, Profile } from '@/types/domain';
import { professions } from '@/data/professions';

/** Pick the right language out of a bilingual value. */
export function localized(value: Bilingual, locale: Locale): string {
  return locale === 'th' ? value.th : value.en;
}

/**
 * Locale-aware date. Thai uses the Buddhist calendar (so 2026 → พ.ศ. 2569) and
 * Thai month names; English uses day-month-year. Accepts an ISO `YYYY-MM-DD`
 * string (parsed as a local date) or a Date.
 */
export function formatDate(
  value: string | Date | undefined | null,
  locale: Locale,
  opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' },
): string {
  if (!value) return '';
  let d: Date | null;
  if (value instanceof Date) {
    d = value;
  } else {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : Number.isNaN(new Date(value).getTime()) ? null : new Date(value);
  }
  if (!d) return '';
  return new Intl.DateTimeFormat(locale === 'th' ? 'th-TH-u-ca-buddhist' : 'en-GB', opts).format(d);
}

/** Full legal name in the requested language (no title). */
export function fullName(p: Profile, locale: Locale): string {
  return locale === 'th' ? `${p.firstTh} ${p.lastTh}` : `${p.firstEn} ${p.lastEn}`;
}

/**
 * Full name WITH professional title, rendered per locale:
 *  - Thai → "{prefix} {first} {last}"  (e.g. พว. ศิริพร เจริญ · นพ. ณภัทร วงศ์)
 *  - English → "{prefix} {first} {last}, {designation}"
 *      (e.g. Siriporn Charoen, RN · Dr. Naphat Wong)
 * The Thai prefix never appears in English; the RN/ENP designation never
 * appears in Thai. The English prefix is derived from the profession catalog.
 */
export function titledName(p: Profile, locale: Locale): string {
  if (locale === 'th') {
    return [p.titlePrefix, p.firstTh, p.lastTh].filter(Boolean).join(' ').trim();
  }
  const enPrefix = p.profession
    ? (professions[p.profession]?.prefixes.find((x) => x.th === p.titlePrefix)?.en ?? '')
    : '';
  const base = [enPrefix, p.firstEn, p.lastEn].filter(Boolean).join(' ').trim();
  return p.titleSuffix ? `${base}, ${p.titleSuffix}` : base;
}

/** "31 / 40" style ratio. */
export function ratio(part: number, total: number): string {
  return `${part} / ${total}`;
}

/** Percent string, clamped. */
export function pct(value: number): string {
  return `${Math.max(0, Math.min(100, Math.round(value)))}%`;
}

/** Online-content duration from total lesson minutes → "1h 40m" / "45 min". */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Initials from an English name. */
export function initialsOf(first: string, last: string): string {
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}
